/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ActionWithMissingDataErrorCode } from "~/generated/graphql";
import { LOG_OUTPUT_LABELS } from "~/constants";
import { SvgName } from "~/tools/types";
import { statusDetails } from "../const";
import { ResourceUpdateStatus } from "../types";
import { UpdateReportItemStatus, type UpdateReportSortField } from "./types";

export enum UpdateReportUiStatus {
  DEFAULT = "default",
  SUCCESS = "success",
  DANGER = "danger",
  WARNING = "warning",
  NOTIFICATION = "notification",
}

export const updateReportStatusConfig: Record<
  UpdateReportItemStatus,
  { label: string; uiStatus: UpdateReportUiStatus }
> = {
  [UpdateReportItemStatus.UPDATED]: {
    label: "Updated",
    uiStatus: UpdateReportUiStatus.SUCCESS,
  },
  [UpdateReportItemStatus.INSTALLED]: {
    label: "Installed",
    uiStatus: UpdateReportUiStatus.SUCCESS,
  },
  [UpdateReportItemStatus.INSTALLED_PENDING_REBOOT]: {
    label: "Pending Reboot",
    uiStatus: UpdateReportUiStatus.DANGER,
  },
  [UpdateReportItemStatus.FAILED]: {
    label: "Failed",
    uiStatus: UpdateReportUiStatus.DANGER,
  },
  [UpdateReportItemStatus.AVAILABLE]: {
    label: "Available",
    uiStatus: UpdateReportUiStatus.DANGER,
  },
  [UpdateReportItemStatus.REMOVED]: {
    label: "Removed",
    uiStatus: UpdateReportUiStatus.SUCCESS,
  },
};

export const updateReportUiStatusToColor: Record<UpdateReportUiStatus, string> =
  {
    [UpdateReportUiStatus.DEFAULT]:
      statusDetails[ResourceUpdateStatus.Total].color,
    [UpdateReportUiStatus.SUCCESS]:
      statusDetails[ResourceUpdateStatus.NoPendingUpdates].color,
    [UpdateReportUiStatus.DANGER]:
      statusDetails[ResourceUpdateStatus.PendingUpdates].color,
    [UpdateReportUiStatus.WARNING]:
      statusDetails[ResourceUpdateStatus.NoUpdatesData].color,
    [UpdateReportUiStatus.NOTIFICATION]:
      statusDetails[ResourceUpdateStatus.InProgress].color,
  };

const updateReportUiStatusToIcon: Record<UpdateReportUiStatus, SvgName> = {
  [UpdateReportUiStatus.DEFAULT]:
    statusDetails[ResourceUpdateStatus.Total].icon,
  [UpdateReportUiStatus.SUCCESS]:
    statusDetails[ResourceUpdateStatus.NoPendingUpdates].icon,
  [UpdateReportUiStatus.DANGER]:
    statusDetails[ResourceUpdateStatus.PendingUpdates].icon,
  [UpdateReportUiStatus.WARNING]:
    statusDetails[ResourceUpdateStatus.NoUpdatesData].icon,
  [UpdateReportUiStatus.NOTIFICATION]:
    statusDetails[ResourceUpdateStatus.InProgress].icon,
};

export const getUpdateReportStatusDisplay = (
  status: UpdateReportItemStatus
) => {
  const { label, uiStatus } = updateReportStatusConfig[status];

  return {
    label,
    uiStatus,
    color: updateReportUiStatusToColor[uiStatus],
    icon: updateReportUiStatusToIcon[uiStatus],
  };
};

export const updateReportColumnLabels: Record<UpdateReportSortField, string> = {
  name: "Update Name",
  time: "Time",
  patchPolicy: "Patch Policy",
  rebootAllowed: "Reboot Allowed",
  releaseDate: "Release Date",
  category: "Classification",
  status: "Update Status",
};

export const NA_LABEL = "N/A";

export const missingActionLogsSections = [
  {
    key: ActionWithMissingDataErrorCode.LogsMalformed,
    title: LOG_OUTPUT_LABELS.malformed,
  },
  {
    key: ActionWithMissingDataErrorCode.LogsNotAvailable,
    title: LOG_OUTPUT_LABELS.unavailable,
  },
] as const;
